import {
  BoxGeometry,
  Color,
  ConeGeometry,
  CylinderGeometry,
  Group,
  LatheGeometry,
  type Material,
  Mesh,
  PlaneGeometry,
  PointLight,
  SphereGeometry,
  TorusGeometry,
  Vector2,
} from 'three';
import { EMIT, LIGHTS } from '../render/palette';
import {
  MATERIALS,
  applyContactShading,
  emitter,
  flatVertexColor,
  seeded,
  signMaterial,
  tintVertexColor,
} from '../render/materials';
import { lightPool } from './props';

/**
 * EVERYTHING OUTSIDE THE SHELL.
 *
 * The building used to stop at its own walls. Walk south out of the forecourt
 * and the tarmac ended at a line, the sky met the ground with nothing in
 * between, and the character controller fell through the gap — which is the
 * single most prototype-shaped thing a game can do. It also meant the mall had
 * no roof: from any raised angle it was an open-topped box you could look down
 * into, and the roofline against the sky was a cut rather than a building.
 *
 * So this file is the two halves of the same problem. Below the shell: a car
 * park with a real edge to it, things parked in it, and a horizon behind that.
 * Above the shell: a deck, a parapet, and the plant a building of this size
 * actually carries — which is also what gives the bear something to sit on.
 *
 * Same conventions as `props.ts`: authored at the origin, footprint centred,
 * sitting ON y = 0, and every geometry carries vertex colour before it is
 * handed to a shared material.
 */

/** Tint a geometry and wrap it in a mesh that both casts and takes shadow. */
function part(geo: Parameters<typeof tintVertexColor>[0], mat: Material, tint: number): Mesh {
  applyContactShading(geo, { falloff: 0.9, strength: 0.28 });
  tintVertexColor(geo, tint);
  const m = new Mesh(geo, mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** Position and rotate in one call, the way the loot builders do. */
function at<T extends Mesh | Group>(o: T, x: number, y: number, z: number, rx = 0, ry = 0, rz = 0): T {
  o.position.set(x, y, z);
  o.rotation.set(rx, ry, rz);
  return o;
}

/**
 * Aircraft-warning red. Not in the palette because nothing inside the building
 * is ever this colour — it exists solely as the one saturated pixel on the
 * skyline, and the palette's job is to stop that colour leaking anywhere else.
 */
const BEACON_RED = new Color(0xff4436);

const CARD = () => MATERIALS.card();
const DARK = () => MATERIALS.steelDark();
const STEEL = () => MATERIALS.steel();
const CONCRETE = () => MATERIALS.concreteDark();
const RUBBER = () => MATERIALS.rubber();

// ---------------------------------------------------------------------------
// The roof
// ---------------------------------------------------------------------------

/**
 * Air-handling plant. The single most recognisable object on any commercial
 * roof, and the reason a roofline reads as a working building rather than a
 * lid: the silhouette is a row of squat boxes with round holes in the top.
 */
export function condenserUnit(w = 2.4, h = 1.3, d = 1.8): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(w, h, d), CARD(), 0x878d94), 0, h / 2 + 0.12, 0));
  // Feet, so it sits on rails rather than melting into the deck.
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(w * 0.9, 0.12, 0.16), DARK(), 0x2f333a), 0, 0.06, s * d * 0.35));
  }
  // Louvred sides. Three bands is enough to break the box up at range.
  for (let i = 0; i < 3; i++) {
    g.add(
      at(part(new BoxGeometry(w * 0.86, 0.06, d + 0.03), CARD(), 0x5f656d), 0, h * 0.35 + i * 0.2 + 0.12, 0),
    );
  }
  // Fan cowls in the top, which is the shape you actually recognise from below.
  const fans = Math.max(1, Math.round(w / 1.3));
  for (let i = 0; i < fans; i++) {
    const x = (i - (fans - 1) / 2) * (w / fans);
    g.add(at(part(new CylinderGeometry(w / fans / 2.6, w / fans / 2.6, 0.14, 10), DARK(), 0x3b4048), x, h + 0.16, 0));
    for (let b = 0; b < 3; b++) {
      g.add(
        at(
          part(new BoxGeometry(w / fans / 1.5, 0.02, 0.14), DARK(), 0x585f68),
          x,
          h + 0.2,
          0,
          0,
          (b * Math.PI) / 3,
          0.18,
        ),
      );
    }
  }
  return g;
}

/** Stair head — the little hut the roof access comes up through. */
export function stairPenthouse(w = 3.0, h = 2.6, d = 2.6): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(w, h, d), CARD(), 0x6d737a), 0, h / 2, 0));
  // A coping slab a shade wider than the hut, so it has an eaves line.
  g.add(at(part(new BoxGeometry(w + 0.3, 0.16, d + 0.3), CONCRETE(), 0x4c5158), 0, h + 0.08, 0));
  g.add(at(part(new BoxGeometry(0.9, 2.0, 0.08), DARK(), 0x33383f), 0, 1.0, d / 2 + 0.03));
  g.add(at(part(new BoxGeometry(0.06, 0.06, 0.1), STEEL(), 0x9aa2ab), 0.32, 1.0, d / 2 + 0.09));
  // A bulkhead light over the door, because there always is one and it is the
  // only thing giving the hut an edge at night.
  g.add(at(new Mesh(new PlaneGeometry(0.3, 0.16), emitter('roofBulkhead', EMIT.fluoro, 1.4)), 0, 2.16, d / 2 + 0.05));
  return g;
}

/** Satellite dish on a tripod. Reads at any distance and costs almost nothing. */
export function satelliteDish(radius = 1.1): Group {
  const g = new Group();
  const profile: Vector2[] = [];
  for (let i = 0; i <= 6; i++) {
    const r = (i / 6) * radius;
    profile.push(new Vector2(r, (r * r) / (radius * 1.9)));
  }
  const face = part(new LatheGeometry(profile, 12), CARD(), 0x9aa0a8);
  // Tipped back and turned off-axis: a dish aimed dead level at the camera is a
  // white disc, and a white disc is not a dish.
  const yoke = at(new Group(), 0, 1.5, 0, -0.9, 0.6, 0);
  yoke.add(face);
  yoke.add(at(part(new CylinderGeometry(0.035, 0.035, radius * 0.9, 6), DARK(), 0x33383f), 0, radius * 0.42, 0));
  yoke.add(at(part(new BoxGeometry(0.12, 0.16, 0.12), DARK(), 0x2b3037), 0, radius * 0.85, 0));
  g.add(yoke);
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    g.add(
      at(
        part(new CylinderGeometry(0.04, 0.04, 1.7, 5), DARK(), 0x3b4048),
        Math.cos(a) * 0.32,
        0.8,
        Math.sin(a) * 0.32,
        Math.sin(a) * 0.36,
        0,
        -Math.cos(a) * 0.36,
      ),
    );
  }
  g.add(at(part(new BoxGeometry(1.1, 0.1, 1.1), CONCRETE(), 0x4a4f56), 0, 0.05, 0));
  return g;
}

/** Lattice mast with an aircraft-warning beacon on it. */
export function aerialMast(height = 5.5): Group {
  const g = new Group();
  g.add(at(part(new CylinderGeometry(0.06, 0.09, height, 6), DARK(), 0x3b4048), 0, height / 2, 0));
  for (const y of [0.35, 0.6, 0.82]) {
    g.add(at(part(new BoxGeometry(0.9, 0.04, 0.04), DARK(), 0x4a5058), 0, height * y, 0));
    g.add(at(part(new BoxGeometry(0.04, 0.04, 0.9), DARK(), 0x4a5058), 0, height * y, 0));
  }
  g.add(at(part(new BoxGeometry(0.5, 0.12, 0.5), CONCRETE(), 0x4a4f56), 0, 0.06, 0));
  // The beacon. One red pixel on the skyline does more for the sense that this
  // is a real place in a real town than any amount of geometry.
  const beacon = new Mesh(new SphereGeometry(0.1, 6, 5), emitter('mastBeacon', BEACON_RED, 2.2));
  beacon.position.y = height + 0.08;
  g.add(beacon);
  return g;
}

/**
 * Floodlight on a short stand, aimed up.
 *
 * Exists for one job: the thing on the roof has to be lit from below, because a
 * silhouette against a night sky is a hole and an uplit object against a night
 * sky is a landmark. The real light is cheap here — it is baked, and the only
 * geometry within reach of it is the roof deck and the bear.
 */
export function roofFlood(tilt = 0.9, drive = 1): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(0.42, 0.08, 0.32), CONCRETE(), 0x44494f), 0, 0.04, 0));
  g.add(at(part(new CylinderGeometry(0.05, 0.05, 0.5, 6), DARK(), 0x33383f), 0, 0.28, 0));
  const head = at(new Group(), 0, 0.56, 0, -tilt, 0, 0);
  head.add(at(part(new BoxGeometry(0.4, 0.3, 0.16), DARK(), 0x2f333a), 0, 0, 0));
  head.add(at(new Mesh(new PlaneGeometry(0.34, 0.24), emitter('roofFloodLens', EMIT.sodium, 2.0)), 0, 0, 0.085));
  g.add(head);
  /**
   * Bright, wide, and slow to fall off, and all three are deliberate.
   *
   * The first version of this ran at 14/16/1.6 and the seven-metre object it
   * was supposed to be lighting came out a flat black silhouette from every
   * camera in the game — six metres of inverse-square at decay 1.6 eats
   * three quarters of it before it arrives. A flood is a *reflector*: it
   * throws a collimated beam that barely attenuates over the twenty metres it
   * is aimed across, which is nothing like a bare bulb, and the exponent is
   * the only knob that models the difference. All of these are baked, so the
   * cost of the extra reach is trace time at build and zero at runtime.
   */
  const l = new PointLight(LIGHTS.sodium.color, 52 * drive, 24, 1.35);
  l.position.set(0, 0.95, 0.55);
  g.add(l);
  return g;
}

// ---------------------------------------------------------------------------
// The car park
// ---------------------------------------------------------------------------

/**
 * A parked car, in three body styles.
 *
 * The point of these is not the cars. An empty car park at night is a car park
 * that has been emptied, which is a fact about the world; a car park with four
 * cars still in it at 2am is a car park with four people unaccounted for, which
 * is a mood. Four is roughly the right number.
 *
 * Built along +X, so a yaw of 0 points the bonnet east.
 */
export function parkedCar(kind: 'hatch' | 'estate' | 'van' = 'hatch', tint = 0x4a5058): Group {
  const g = new Group();
  const L = kind === 'van' ? 5.1 : kind === 'estate' ? 4.6 : 4.0;
  const W = kind === 'van' ? 2.0 : 1.78;
  const sillY = 0.44;

  // Body. Two boxes rather than one: the lower band is the sill and it is always
  // a darker, dirtier colour than the panel above it, which is what stops a car
  // reading as a single extruded rectangle.
  g.add(at(part(new BoxGeometry(L, 0.22, W - 0.1), CARD(), 0x22262b), 0, sillY - 0.11, 0));
  g.add(at(part(new BoxGeometry(L, 0.5, W), CARD(), tint), 0, sillY + 0.25, 0));
  // Bonnet and boot lids, half a step down from the waistline.
  g.add(at(part(new BoxGeometry(L * 0.3, 0.1, W - 0.08), CARD(), tint), L * 0.33, sillY + 0.53, 0));
  if (kind !== 'van') {
    g.add(at(part(new BoxGeometry(L * 0.22, 0.14, W - 0.08), CARD(), tint), -L * 0.38, sillY + 0.55, 0));
  }

  // Cabin.
  const cabL = kind === 'van' ? L * 0.34 : kind === 'estate' ? L * 0.52 : L * 0.46;
  const cabX = kind === 'van' ? -L * 0.12 : -L * 0.06;
  const cabH = kind === 'van' ? 1.05 : 0.62;
  const roofY = sillY + 0.5 + cabH;
  if (kind === 'van') {
    // A van is a box with a nose, so the load body is the cabin.
    g.add(at(part(new BoxGeometry(L * 0.62, cabH, W), CARD(), tint), -L * 0.16, sillY + 0.5 + cabH / 2, 0));
    g.add(at(part(new BoxGeometry(L * 0.3, cabH * 0.62, W - 0.06), CARD(), tint), L * 0.24, sillY + 0.5 + cabH * 0.31, 0));
    g.add(at(part(new BoxGeometry(0.06, cabH * 0.5, W - 0.16), MATERIALS.gloss(), 0x1b222b), L * 0.4, sillY + 0.5 + cabH * 0.34, 0, 0, 0, -0.28));
  } else {
    g.add(at(part(new BoxGeometry(cabL, cabH, W - 0.12), CARD(), tint), cabX, sillY + 0.5 + cabH / 2, 0));
    // Windscreen and backlight, raked. Glossy and near-black: at night glass is
    // a hard specular and nothing else.
    g.add(at(part(new BoxGeometry(0.06, cabH * 1.05, W - 0.2), MATERIALS.gloss(), 0x161c24), cabX + cabL / 2 + 0.14, sillY + 0.5 + cabH / 2, 0, 0, 0, -0.42));
    g.add(at(part(new BoxGeometry(0.06, cabH * 1.0, W - 0.24), MATERIALS.gloss(), 0x161c24), cabX - cabL / 2 - 0.1, sillY + 0.5 + cabH / 2, 0, 0, 0, 0.36));
    for (const s of [-1, 1]) {
      g.add(at(part(new BoxGeometry(cabL - 0.2, cabH - 0.16, 0.04), MATERIALS.gloss(), 0x161c24), cabX, sillY + 0.5 + cabH / 2, (s * (W - 0.12)) / 2));
    }
    g.add(at(part(new BoxGeometry(cabL + 0.1, 0.07, W - 0.1), CARD(), tint), cabX, roofY, 0));
  }

  // Wheels, tucked under the arches.
  for (const x of [L * 0.32, -L * 0.31]) {
    for (const s of [-1, 1]) {
      const w = at(part(new CylinderGeometry(0.33, 0.33, 0.2, 10), RUBBER(), 0x1c1e22), x, 0.33, (s * W) / 2 - s * 0.06, Math.PI / 2, 0, 0);
      g.add(w);
      g.add(at(part(new CylinderGeometry(0.17, 0.17, 0.22, 8), STEEL(), 0x767d86), x, 0.33, (s * W) / 2 - s * 0.06, Math.PI / 2, 0, 0));
    }
  }

  // Lamps. Cold glass at the front, dark red at the back — no emission, because
  // nothing here is running.
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(0.06, 0.14, 0.34), MATERIALS.gloss(), 0xb9c4cf), L / 2, sillY + 0.34, (s * (W - 0.5)) / 2));
    g.add(at(part(new BoxGeometry(0.06, 0.18, 0.3), MATERIALS.gloss(), 0x5a1a1e), -L / 2, sillY + 0.36, (s * (W - 0.5)) / 2));
  }
  g.add(at(part(new BoxGeometry(0.1, 0.16, W - 0.3), DARK(), 0x2b3037), L / 2 - 0.02, sillY - 0.02, 0));
  return g;
}

/**
 * The van the crew leaves in. This is the extraction point, made of geometry.
 *
 * It had none. The way out of the round was an invisible four-metre disc on bare
 * tarmac, marked by nothing at all, and a player who did not read the briefing
 * had no way to know it existed — the objective system was doing all the work
 * that the level should have been doing. A van with its back doors open and the
 * interior light on says "here, and bring it here" without a word of UI.
 *
 * Authored along +X with the rear doors at -X, so a yaw of -π/2 points the open
 * back at the mall.
 */
export function getawayVan(): Group {
  const g = new Group();
  const L = 5.6;
  const W = 2.15;
  const bodyH = 1.85;
  const floorY = 0.72;
  const skin = 0x2f3a44;

  /**
   * The load bay is five panels round a hole, not a box.
   *
   * It was a box. The doors swung open on a solid rear wall, so the one thing
   * this prop exists to say — *put the bag in here* — was the one thing it could
   * not say, and the dome light sat sealed inside a closed volume where the bake
   * leaked it out through every face at once and rendered the whole van as a
   * white slab. Hollowing it fixes both: there is somewhere to put the bag, and
   * the light now has exactly one way out, which is the opening you walk into.
   */
  const bayL = L * 0.66;
  const bayX = -L * 0.14;
  const wall = 0.07;
  // Roof, bulkhead, and the two sides. The rear stays open.
  g.add(at(part(new BoxGeometry(bayL, wall, W), CARD(), skin), bayX, floorY + bodyH - wall / 2, 0));
  g.add(at(part(new BoxGeometry(wall, bodyH, W), CARD(), 0x27303a), bayX + bayL / 2, floorY + bodyH / 2, 0));
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(bayL, bodyH, wall), CARD(), skin), bayX, floorY + bodyH / 2, (s * (W - wall)) / 2));
    // Inner face, lighter, so the lit bay reads as a room rather than a cavity.
    g.add(at(part(new BoxGeometry(bayL - 0.1, bodyH - 0.12, 0.02), CARD(), 0x6d6455), bayX, floorY + bodyH / 2, (s * (W - wall)) / 2 - s * 0.06));
  }
  // Corner posts and a header at the opening, so the hole has an edge.
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(0.1, bodyH, 0.12), CARD(), skin), bayX - bayL / 2, floorY + bodyH / 2, (s * (W - 0.12)) / 2));
  }
  g.add(at(part(new BoxGeometry(0.1, 0.16, W), CARD(), skin), bayX - bayL / 2, floorY + bodyH - 0.08, 0));
  g.add(at(part(new BoxGeometry(L * 0.36, bodyH * 0.66, W - 0.06), CARD(), skin), L * 0.3, floorY + bodyH * 0.33, 0));
  g.add(at(part(new BoxGeometry(0.07, bodyH * 0.42, W - 0.2), MATERIALS.gloss(), 0x141a22), L * 0.47, floorY + bodyH * 0.44, 0, 0, 0, -0.3));
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(L * 0.3, bodyH * 0.34, 0.05), MATERIALS.gloss(), 0x141a22), L * 0.3, floorY + bodyH * 0.42, (s * (W - 0.06)) / 2));
  }
  // Load floor and sill, visible through the open back.
  g.add(at(part(new BoxGeometry(L * 0.66, 0.1, W - 0.1), CARD(), 0x4d4335), -L * 0.14, floorY + 0.05, 0));
  g.add(at(part(new BoxGeometry(0.14, 0.3, W), DARK(), 0x23272d), -L * 0.47, floorY - 0.1, 0));
  // Chassis and wheels.
  g.add(at(part(new BoxGeometry(L, 0.2, W - 0.4), DARK(), 0x1e2126), 0, floorY - 0.14, 0));
  for (const x of [L * 0.3, -L * 0.26]) {
    for (const s of [-1, 1]) {
      g.add(at(part(new CylinderGeometry(0.42, 0.42, 0.24, 10), RUBBER(), 0x1c1e22), x, 0.42, (s * W) / 2 - s * 0.1, Math.PI / 2, 0, 0));
      g.add(at(part(new CylinderGeometry(0.2, 0.2, 0.26, 8), STEEL(), 0x6f767f), x, 0.42, (s * W) / 2 - s * 0.1, Math.PI / 2, 0, 0));
    }
  }

  // Both rear doors swung wide. The angle is the whole prop: at anything under
  // about a hundred degrees they read as a closed van seen from a bad angle.
  for (const s of [-1, 1]) {
    const hinge = at(new Group(), -L * 0.47, floorY + bodyH / 2, (s * W) / 2);
    hinge.rotation.y = s * 2.0;
    hinge.add(at(part(new BoxGeometry(0.07, bodyH - 0.06, W / 2), CARD(), skin), 0, 0, (-s * W) / 4));
    hinge.add(at(part(new BoxGeometry(0.05, 0.16, 0.16), STEEL(), 0x8a929b), 0.06, 0, (-s * W) / 2 + s * 0.12));
    g.add(hinge);
  }

  // Interior light, and the reason the pad is findable from across the apron.
  const dome = new Mesh(new PlaneGeometry(W - 0.5, 1.4), emitter('vanDome', EMIT.shopWarm, 1.0));
  dome.rotation.set(Math.PI / 2, 0, Math.PI / 2);
  dome.position.set(-L * 0.16, floorY + bodyH - 0.06, 0);
  g.add(dome);
  const inner = new PointLight(LIGHTS.shop.color, 6.5, 9, 1.7);
  inner.position.set(-L * 0.2, floorY + 1.1, 0);
  g.add(inner);
  g.add(at(lightPool(4.2, EMIT.shopWarm, 0.3, 'van'), -L * 0.7, 0, 0));

  /**
   * Hazards on the rear corners, and a beacon on the cab roof.
   *
   * The first pass ran the hazards at 1.6 on a flat rear wall and they read as
   * headlights — two hot lamps at bumper height on a pale slab is a car coming
   * at you, not a van waiting for you. Half the fix was hollowing the bay so
   * there is a dark opening between them. The rest is turning them down until
   * they are markers rather than sources, and moving the loud one up onto the
   * roof where a waiting vehicle actually puts it.
   */
  for (const s of [-1, 1]) {
    const h = new Mesh(new PlaneGeometry(0.13, 0.2), emitter('vanHazard', EMIT.signAmber, 0.85));
    h.rotation.y = -Math.PI / 2;
    h.position.set(-L * 0.475, floorY + 0.3, (s * (W - 0.28)) / 2);
    g.add(h);
  }
  g.add(at(part(new BoxGeometry(0.3, 0.06, 0.5), DARK(), 0x23272d), L * 0.3, floorY + bodyH * 0.66 + 0.03, 0));
  const beacon = new Mesh(new CylinderGeometry(0.11, 0.13, 0.16, 8), emitter('vanBeacon', EMIT.signAmber, 1.4));
  g.add(at(beacon, L * 0.3, floorY + bodyH * 0.66 + 0.14, 0));
  /**
   * Duffels already in the back, so it looks like a job in progress.
   *
   * Tinted well up from a real canvas bag. They sit on a warm lit floor with a
   * dark bulkhead behind them, and at anything close to their honest value they
   * came back as two black lumps — the bay reads as empty, which is the opposite
   * of the point. A strap over the top is what turns the lump into a bag.
   */
  for (const [x, z, yaw, t] of [
    [-1.55, -0.42, 0.28, 0x77704f],
    [-2.15, 0.4, -0.5, 0x5c6570],
  ] as const) {
    const bag = new Group();
    const body = part(new SphereGeometry(0.34, 8, 6), MATERIALS.velvet(), t);
    body.scale.set(1.45, 0.7, 0.88);
    bag.add(body);
    const strap = part(new TorusGeometry(0.26, 0.028, 4, 12), MATERIALS.velvet(), 0x2f3239);
    bag.add(at(strap, 0, 0.02, 0, 0, Math.PI / 2, 0));
    for (const s of [-1, 1]) {
      bag.add(at(part(new CylinderGeometry(0.02, 0.02, 0.2, 5), MATERIALS.velvet(), 0x2f3239), s * 0.16, 0.26, 0, 0, 0, Math.PI / 2));
    }
    bag.add(at(part(new BoxGeometry(0.06, 0.05, 0.02), STEEL(), 0x9aa2ab), 0.42, 0.06, 0.3));
    g.add(at(bag, x, floorY + 0.32, z, 0, yaw, 0));
  }
  return g;
}

/**
 * A run of chain-link on posts. Length along X.
 *
 * This is the edge of the world, and it is deliberately a *see-through* edge.
 * A solid wall would have done the job of stopping the player and none of the
 * job of making the far side exist — half the value of putting a horizon out
 * there is being able to look at it through the thing that stops you.
 */
export function chainLinkFence(length: number, height = 2.4, opts: { barbed?: boolean } = {}): Group {
  const g = new Group();
  const posts = Math.max(2, Math.round(length / 3));
  for (let i = 0; i <= posts; i++) {
    const x = -length / 2 + (length / posts) * i;
    g.add(at(part(new CylinderGeometry(0.05, 0.055, height, 6), STEEL(), 0x7d858e), x, height / 2, 0));
  }
  for (const y of [height - 0.06, 0.12]) {
    g.add(at(part(new CylinderGeometry(0.035, 0.035, length, 5), STEEL(), 0x7d858e), 0, y, 0, 0, 0, Math.PI / 2));
  }

  // The mesh itself. UVs are set from the run length so the diamonds stay the
  // same size whether the panel is three metres or thirty — a fence whose weave
  // stretches with the wall is the giveaway that it is a texture.
  const panel = new PlaneGeometry(length, height - 0.2);
  flatVertexColor(panel);
  const uv = panel.getAttribute('uv');
  for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (length / 0.34), uv.getY(i) * ((height - 0.2) / 0.34));
  const mesh = new Mesh(panel, MATERIALS.chainLink());
  mesh.position.y = height / 2;
  g.add(mesh);

  if (opts.barbed) {
    for (const s of [-1, 1]) {
      for (let i = 0; i <= posts; i++) {
        const x = -length / 2 + (length / posts) * i;
        g.add(at(part(new CylinderGeometry(0.03, 0.03, 0.5, 4), STEEL(), 0x7d858e), x, height + 0.2, s * 0.1, s * 0.5, 0, 0));
      }
      for (let k = 0; k < 3; k++) {
        g.add(at(part(new CylinderGeometry(0.014, 0.014, length, 4), STEEL(), 0x8d959e), 0, height + 0.12 + k * 0.16, s * (0.06 + k * 0.08), 0, 0, Math.PI / 2));
      }
    }
  }
  return g;
}

/**
 * Roadside totem at the car park entrance.
 *
 * The one piece of geometry out here that is taller than it is wide, which is
 * the entire reason it is worth building: a flat apron with a hundred-metre
 * horizon needs one vertical to give the eye a sense of how big any of it is.
 */
export function pylonSign(height = 9): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(2.0, 0.4, 1.0), CONCRETE(), 0x4a4f56), 0, 0.2, 0));
  for (const s of [-1, 1]) {
    g.add(at(part(new BoxGeometry(0.28, height - 2.6, 0.4), DARK(), 0x33383f), s * 0.62, (height - 2.6) / 2 + 0.4, 0));
  }
  const headH = 2.6;
  const headY = height - headH / 2;
  g.add(at(part(new BoxGeometry(3.6, headH, 0.5), DARK(), 0x2b3037), 0, headY, 0));
  for (const s of [-1, 1]) {
    const face = new Mesh(
      new PlaneGeometry(3.3, headH - 0.3),
      signMaterial({
        text: 'MERIDIAN\nCROSSING',
        widthMetres: 3.3,
        heightMetres: headH - 0.3,
        color: EMIT.signCyan,
        font: 'wide',
        pixelsPerMetre: 96,
      }),
    );
    face.position.set(0, headY, s * 0.26);
    face.rotation.y = s > 0 ? 0 : Math.PI;
    g.add(face);
  }
  // A smaller panel under it carrying the deadpan half of the message.
  const subY = height - headH - 1.1;
  g.add(at(part(new BoxGeometry(3.0, 1.5, 0.4), DARK(), 0x2b3037), 0, subY, 0));
  for (const s of [-1, 1]) {
    const face = new Mesh(
      new PlaneGeometry(2.7, 1.2),
      signMaterial({
        text: 'FREE PARKING\nWHILE YOU SHOP',
        widthMetres: 2.7,
        heightMetres: 1.2,
        color: EMIT.signAmber,
        font: 'wide',
      }),
    );
    face.position.set(0, subY, s * 0.21);
    face.rotation.y = s > 0 ? 0 : Math.PI;
    g.add(face);
  }
  return g;
}

/** Nested trolleys in a bay. Reads as a car park from three hundred metres. */
export function trolleyBay(count = 7): Group {
  const g = new Group();
  // The shelter: two frames and a shallow roof.
  for (const s of [-1, 1]) {
    g.add(at(part(new CylinderGeometry(0.06, 0.06, 2.4, 6), STEEL(), 0x7d858e), s * 1.5, 1.2, -0.9));
    g.add(at(part(new CylinderGeometry(0.06, 0.06, 2.4, 6), STEEL(), 0x7d858e), s * 1.5, 1.2, 0.9));
  }
  g.add(at(part(new BoxGeometry(3.4, 0.1, 2.2), CARD(), 0x767d86), 0, 2.45, 0, 0.06, 0, 0));
  g.add(at(part(new BoxGeometry(3.2, 0.5, 0.08), DARK(), 0x33383f), 0, 2.15, -1.0));

  // The nest. Each trolley is a wire basket on a chassis, pushed 0.32 into the
  // one in front, which is the spacing that makes a queue of them read as one
  // object with a repeating edge rather than as N separate props.
  for (let i = 0; i < count; i++) {
    const z = -0.9 + i * 0.32;
    const t = at(new Group(), 0, 0, z);
    t.add(at(part(new BoxGeometry(0.58, 0.34, 0.7), STEEL(), 0x9aa2ab), 0, 0.78, 0));
    t.add(at(part(new BoxGeometry(0.5, 0.03, 0.62), STEEL(), 0x848c95), 0, 0.6, 0));
    t.add(at(part(new BoxGeometry(0.56, 0.05, 0.05), STEEL(), 0xa8b0b9), 0, 1.0, 0.36));
    for (const s of [-1, 1]) {
      t.add(at(part(new CylinderGeometry(0.025, 0.025, 0.62, 5), STEEL(), 0x848c95), (s * 0.5) / 2, 0.31, 0.28));
      t.add(at(part(new CylinderGeometry(0.05, 0.05, 0.05, 6), RUBBER(), 0x1e2126), (s * 0.5) / 2, 0.05, 0.28, 0, 0, Math.PI / 2));
      t.add(at(part(new CylinderGeometry(0.05, 0.05, 0.05, 6), RUBBER(), 0x1e2126), (s * 0.5) / 2, 0.05, -0.28, 0, 0, Math.PI / 2));
    }
    g.add(t);
  }
  return g;
}

/**
 * A dark block on the far side of the fence, with a few lights left on.
 *
 * Not a building anyone can reach — a horizon. Everything about it is silhouette
 * plus a scatter of small warm rectangles, because that is exactly how much of a
 * distant building survives a night sky.
 */
export function distantBlock(w: number, h: number, d: number, seed = 1): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(w, h, d), CARD(), 0x2c333c), 0, h / 2, 0));
  g.add(at(part(new BoxGeometry(w + 0.5, 0.5, d + 0.5), CARD(), 0x252b33), 0, h + 0.25, 0));
  const r = seeded(seed);
  /**
   * Windows on all four elevations, and brighter than they look like they need
   * to be.
   *
   * The first version lit only the +Z face, which is the one nothing in the game
   * can see — every camera out here is south of these blocks looking north, so
   * they came back as five blank pale slabs standing in the fog. Fog is the real
   * antagonist at this range: forty metres of it washes any albedo you give the
   * concrete to within a few values of the sky, so a lit window is the only mark
   * that survives, and the whole silhouette has to be carried by them.
   */
  for (const [face, span] of [
    [0, w],
    [Math.PI, w],
    [Math.PI / 2, d],
    [-Math.PI / 2, d],
  ] as const) {
    const cols = Math.max(2, Math.floor(span / 3.2));
    const rows = Math.max(1, Math.floor(h / 3.4));
    const out = (Math.abs(Math.sin(face)) > 0.5 ? w : d) / 2 + 0.02;
    for (let i = 0; i < cols; i++) {
      for (let j = 0; j < rows; j++) {
        if (r() > 0.3) continue;
        const warm = r() > 0.5;
        const pane = new Mesh(
          new PlaneGeometry(1.5, 1.2),
          emitter(warm ? 'blockWarm' : 'blockCold', warm ? EMIT.shopWarm : EMIT.fluoro, 0.34),
        );
        const u = (i - (cols - 1) / 2) * 3.2;
        pane.position.set(Math.cos(face) * u + Math.sin(face) * out, 2.2 + j * 3.4, -Math.sin(face) * u + Math.cos(face) * out);
        pane.rotation.y = face;
        g.add(pane);
      }
    }
  }
  // Roof clutter, purely for the skyline. A rectangle ending in a clean edge is
  // the single most model-looking thing you can put on a horizon.
  g.add(at(part(new BoxGeometry(w * 0.22, 2.4, d * 0.3), CARD(), 0x272d35), w * 0.2, h + 1.7, 0));
  g.add(at(part(new CylinderGeometry(1.1, 1.1, 3.2, 8), CARD(), 0x2a3039), -w * 0.24, h + 2.1, d * 0.12));
  g.add(at(part(new CylinderGeometry(0.06, 0.06, h * 0.4, 4), DARK(), 0x1d2127), -w * 0.3, h + h * 0.2 + 0.5, -d * 0.2));
  const beacon = new Mesh(new SphereGeometry(0.22, 6, 5), emitter('blockBeacon', BEACON_RED, 2.4));
  beacon.position.set(-w * 0.3, h + h * 0.4 + 0.5, -d * 0.2);
  g.add(beacon);
  return g;
}

/** A row of dark conifers. Pure silhouette; nothing here is meant to be looked at. */
export function treeLine(length: number, seed = 7): Group {
  const g = new Group();
  const r = seeded(seed);
  const n = Math.max(2, Math.round(length / 3.4));
  for (let i = 0; i < n; i++) {
    const x = -length / 2 + (length / (n - 1 || 1)) * i + (r() - 0.5) * 1.4;
    const h = 5 + r() * 4;
    g.add(at(part(new CylinderGeometry(0.12, 0.2, h * 0.3, 5), MATERIALS.timber(), 0x2a2620), x, h * 0.15, (r() - 0.5) * 2.4));
    for (let k = 0; k < 3; k++) {
      const y = h * (0.28 + k * 0.22);
      g.add(
        at(
          part(new ConeGeometry(h * (0.2 - k * 0.045), h * 0.34, 6), MATERIALS.foliage(), 0x232c26),
          x,
          y + h * 0.17,
          (r() - 0.5) * 2.4,
        ),
      );
    }
  }
  return g;
}

/** Painted bay dividers for the outer lot, as one flat mesh per run. */
export function bayLines(count: number, pitch = 2.6, depth = 5.0): Group {
  const g = new Group();
  const mat = emitter('bayLine', EMIT.torch, 0.1);
  for (let i = 0; i <= count; i++) {
    const line = new Mesh(new PlaneGeometry(0.12, depth), mat);
    line.rotation.x = -Math.PI / 2;
    line.position.set((i - count / 2) * pitch, 0.012, 0);
    g.add(line);
  }
  return g;
}

/** A wheel stop / kerb block, the thing that makes tarmac read as a bay. */
export function kerbBlock(width = 1.7): Group {
  const g = new Group();
  g.add(at(part(new BoxGeometry(width, 0.14, 0.18), CONCRETE(), 0x777d84), 0, 0.07, 0));
  return g;
}

/** Steel gate leaves across the lot entrance, chained shut. */
export function lotGate(width = 7, height = 2.4): Group {
  const g = new Group();
  for (const s of [-1, 1]) {
    const leaf = at(new Group(), (s * width) / 2, 0, 0);
    leaf.add(at(part(new CylinderGeometry(0.07, 0.07, height, 6), STEEL(), 0x8a929b), 0, height / 2, 0));
    leaf.add(at(part(new CylinderGeometry(0.05, 0.05, width / 2, 5), STEEL(), 0x8a929b), (-s * width) / 4, height - 0.08, 0, 0, 0, Math.PI / 2));
    leaf.add(at(part(new CylinderGeometry(0.05, 0.05, width / 2, 5), STEEL(), 0x8a929b), (-s * width) / 4, 0.14, 0, 0, 0, Math.PI / 2));
    const panel = new PlaneGeometry(width / 2, height - 0.3);
    flatVertexColor(panel);
    const uv = panel.getAttribute('uv');
    for (let i = 0; i < uv.count; i++) uv.setXY(i, uv.getX(i) * (width / 2 / 0.34), uv.getY(i) * ((height - 0.3) / 0.34));
    const m = new Mesh(panel, MATERIALS.chainLink());
    m.position.set((-s * width) / 4, height / 2, 0);
    leaf.add(m);
    g.add(leaf);
  }
  // The chain and padlock in the middle, which is the detail that says shut
  // rather than just closed.
  g.add(at(part(new TorusGeometry(0.16, 0.02, 4, 10), STEEL(), 0x767d86), 0, 1.2, 0, Math.PI / 2, 0, 0));
  g.add(at(part(new BoxGeometry(0.1, 0.14, 0.05), STEEL(), 0x9aa2ab), 0, 1.06, 0));
  return g;
}
